import React, { useEffect, useState } from "react";
import api, { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { BarChart3, Users, Eye, MessageCircle, Download, Calendar } from "lucide-react";

/**
 * AnalyticsTab — Phase B Feature 8.
 *
 * Reusable per-project sales analytics view. Mounted from both
 *   /studio/projects/:pid  (admin)  → endpointBase = "/admin"
 *   /portal/projects/:pid  (client) → endpointBase = "/client"
 * The two endpoints return the same payload shape so this single
 * component renders both surfaces.
 *
 * No charts in this phase (per user direction). Tables + KPI cards only.
 */
export default function AnalyticsTab({ projectId, endpointBase = "/admin" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFrom) params[endpointBase === "/admin" ? "from" : "date_from"] = `${dateFrom}T00:00:00Z`;
      if (dateTo) params[endpointBase === "/admin" ? "to" : "date_to"] = `${dateTo}T23:59:59Z`;
      const r = await api.get(`${endpointBase}/projects/${projectId}/analytics`, { params });
      setData(r.data);
    } catch (e) {
      toast.error(apiErrorMessage(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [projectId, dateFrom, dateTo]);

  if (loading && !data) return <div className="text-sm text-zinc-500" data-testid="analytics-loading">Loading analytics…</div>;
  if (!data) return null;

  const t = data.totals || {};
  const inv = data.inventory || {};
  const conv = data.conversion || {};

  return (
    <div className="max-w-6xl space-y-6" data-testid="analytics-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="font-display text-2xl">Sales analytics</div>
          <div className="text-xs text-zinc-500 mt-0.5">Buyer interactions across the entire project. Updates live.</div>
        </div>
        <div className="flex items-center gap-2 text-sm">
          <Calendar className="h-4 w-4 text-zinc-400" />
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2 py-1 rounded border border-zinc-300" data-testid="analytics-from" />
          <span className="text-zinc-400">→</span>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2 py-1 rounded border border-zinc-300" data-testid="analytics-to" />
          {(dateFrom || dateTo) && (
            <button onClick={() => { setDateFrom(""); setDateTo(""); }} className="text-xs text-zinc-500 hover:text-zinc-900">Clear</button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="analytics-kpis">
        <KPI icon={Eye}        label="Project views" value={t.project_view || 0} />
        <KPI icon={BarChart3}  label="Unit detail opens" value={t.unit_detail_open || 0} />
        <KPI icon={Download}   label="PDF downloads" value={t.pdf_download || 0} />
        <KPI icon={Users}      label="Leads" value={data.leads || 0} />
        <KPI icon={MessageCircle} label="WhatsApp clicks" value={t.whatsapp_click || 0} />
        <KPI label="Shortlist adds"  value={t.shortlist_add || 0} />
        <KPI label="Compare opens"   value={t.compare_open || 0} />
        <KPI label="Plan opens"      value={t.plan_open || 0} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ConversionCard label="Detail → Lead" value={conv.detail_to_lead} testid="conv-lead" />
        <ConversionCard label="Detail → WhatsApp" value={conv.detail_to_whatsapp} testid="conv-wa" />
        <InventoryCard inv={inv} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TopUnitsCard title="Top viewed units" rows={data.top_units?.by_detail_opens} testid="top-detail" />
        <TopUnitsCard title="Top shortlisted units" rows={data.top_units?.by_shortlist} testid="top-shortlist" />
        <TopUnitsCard title="Top compared units" rows={data.top_units?.by_compare} testid="top-compare" />
        <TopUnitsCard title="Top lead-generating units" rows={data.top_units?.by_leads} testid="top-leads" />
      </div>

      {data.top_blocks?.length > 0 && (
        <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
          <div className="px-5 py-3 border-b border-zinc-100 text-sm font-medium">Top blocks by interaction</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50/60 text-xs uppercase tracking-wider text-zinc-500">
              <tr><th className="text-left px-5 py-2">Block</th><th className="text-right px-5 py-2">Interactions</th></tr>
            </thead>
            <tbody>
              {data.top_blocks.map((b) => (
                <tr key={b.group_id} className="border-t border-zinc-100" data-testid={`top-block-${b.group_id}`}>
                  <td className="px-5 py-2">{b.name || "—"}</td>
                  <td className="px-5 py-2 text-right font-mono">{b.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function KPI({ icon: Icon, label, value }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] text-zinc-500">
        {Icon && <Icon className="h-3 w-3" />} {label}
      </div>
      <div className="text-2xl font-display text-zinc-900 mt-1.5">{Number(value || 0).toLocaleString()}</div>
    </div>
  );
}

function ConversionCard({ label, value, testid }) {
  const pct = value ? (Number(value) * 100).toFixed(1) : "0.0";
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}</div>
      <div className="text-2xl font-display text-zinc-900 mt-1.5">{pct}%</div>
    </div>
  );
}

function InventoryCard({ inv }) {
  const items = [
    { k: "available", color: "bg-emerald-500" },
    { k: "reserved",  color: "bg-amber-500" },
    { k: "sold",      color: "bg-rose-500" },
    { k: "hidden",    color: "bg-zinc-400" },
  ];
  const total = inv.total || 1;
  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4">
      <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Inventory snapshot</div>
      <div className="text-2xl font-display text-zinc-900 mt-1.5">{inv.total || 0} units</div>
      <div className="mt-3 flex h-2 rounded overflow-hidden bg-zinc-100">
        {items.map((it) => {
          const v = inv[it.k] || 0;
          return v ? <div key={it.k} style={{ width: `${(v / total) * 100}%` }} className={it.color} title={`${it.k}: ${v}`} /> : null;
        })}
      </div>
      <div className="mt-2 flex gap-3 text-[11px] text-zinc-600 flex-wrap">
        {items.map((it) => (
          <div key={it.k} className="flex items-center gap-1">
            <span className={`inline-block w-2 h-2 rounded-sm ${it.color}`} />
            {it.k} {inv[it.k] || 0}
          </div>
        ))}
      </div>
    </div>
  );
}

function TopUnitsCard({ title, rows, testid }) {
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={testid}>
      <div className="px-5 py-3 border-b border-zinc-100 text-sm font-medium">{title}</div>
      {!rows?.length && <div className="px-5 py-6 text-xs text-zinc-500">No data yet.</div>}
      {rows?.length > 0 && (
        <table className="w-full text-sm">
          <tbody>
            {rows.slice(0, 8).map((r) => (
              <tr key={r.entity_id} className="border-t border-zinc-100">
                <td className="px-5 py-2 font-mono text-xs">{r.code}</td>
                <td className="px-5 py-2 text-zinc-600 truncate">{r.name || "—"}</td>
                <td className="px-5 py-2 text-right font-mono">{r.count}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
